import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { DropdownRefProps } from '@cbhq/cds-web/dropdown';

import { NavItem, NavItemChild } from ':cloud/config/navigation';
import { NavigationContext } from ':cloud/contexts/NavigationContext';
import { useGetNavMenuItems } from ':cloud/hooks/useGetNavMenuItems';
import { GenericProviderProps } from ':cloud/types/ts_types';

export default function NavigationProvider({ children }: GenericProviderProps) {
  const notificationRef = useRef<DropdownRefProps>(null);

  const location = useLocation();

  const { allDisplayedNavItems } = useGetNavMenuItems();

  useEffect(() => {
    document.querySelector('.main-container')?.scrollTo(0, 0);
  }, [location.pathname]);

  const getFirstPathSegment = (path: string) => {
    const segments = path.split('/').filter(Boolean);
    return segments.length > 0 ? `/${segments[0]}` : '/';
  };

  const findMatchingNavItem = useCallback(
    (path: string): NavItem | NavItemChild | undefined => {
      const firstSegment = getFirstPathSegment(path);
      return allDisplayedNavItems.find(
        (item) => item.to === firstSegment || path.startsWith(item.to || ''),
      );
    },
    [allDisplayedNavItems],
  );

  const activePage = useMemo(
    () => findMatchingNavItem(location.pathname) || allDisplayedNavItems[0],
    [findMatchingNavItem, location.pathname, allDisplayedNavItems],
  );

  const value = useMemo(
    () => ({
      activePage,
      notificationRef,
    }),
    [activePage],
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}
